import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Building2,
    Plus,
    Pencil,
    Trash2,
    Users,
    Truck,
    Phone,
    Mail,
    MapPin,
} from 'lucide-react';

export default function BomberosCompaniasPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [companias, setCompanias] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [editingCompania, setEditingCompania] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        numero: '',
        nombre: '',
        direccion: '',
        telefono: '',
        email: '',
        fecha_fundacion: '',
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
        } catch (err) {
            console.error('Error loading companias:', err);
            toast.error('Error al cargar compañías');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (compania = null) => {
        if (compania) {
            setEditingCompania(compania);
            setFormData({
                numero: compania.numero || '',
                nombre: compania.nombre || '',
                direccion: compania.direccion || '',
                telefono: compania.telefono || '',
                email: compania.email || '',
                fecha_fundacion: compania.fecha_fundacion || '',
            });
        } else {
            setEditingCompania(null);
            setFormData({
                numero: '',
                nombre: '',
                direccion: '',
                telefono: '',
                email: '',
                fecha_fundacion: '',
            });
        }
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!formData.nombre.trim()) {
            toast.error('El nombre es requerido');
            return;
        }

        try {
            setSaving(true);
            
            // Preparar datos - convertir numero vacío a null
            const dataToSend = {
                ...formData,
                numero: formData.numero === '' || formData.numero === null ? null : parseInt(formData.numero),
            };
            
            if (editingCompania) {
                await bomberosCompaniaAPI.update(editingCompania.id, dataToSend);
                toast.success('Compañía actualizada');
            } else {
                await bomberosCompaniaAPI.create(dataToSend);
                toast.success('Compañía creada');
            }
            setShowDialog(false);
            loadData();
        } catch (err) {
            console.error('Error saving compania:', err);
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (compania) => {
        if (!window.confirm(`¿Está seguro de eliminar la compañía "${compania.nombre}"?`)) return;

        try {
            await bomberosCompaniaAPI.delete(compania.id);
            toast.success('Compañía eliminada');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    if (loading) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-6 p-6 pt-20 md:pt-20">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <Building2 className="h-6 w-6 text-blue-500" />
                                Gestión de Compañías
                            </h1>
                            <p className="text-muted-foreground">Administrar compañías del cuerpo de bomberos</p>
                        </div>
                    </div>
                    {tienePermiso('crear_companias') && (
                        <Button onClick={() => handleOpenDialog()} data-testid="nueva-compania-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Nueva Compañía
                        </Button>
                    )}
                </div>

                {/* Lista de Compañías */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle>Compañías Registradas</CardTitle>
                        <CardDescription>{companias.length} compañía(s) en el sistema</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {companias.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>No hay compañías registradas</p>
                                <p className="text-sm">Cree la primera compañía para comenzar</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-16">N°</TableHead>
                                        <TableHead>Nombre</TableHead>
                                        <TableHead>Dirección</TableHead>
                                        <TableHead>Contacto</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {companias.map((compania) => (
                                        <TableRow key={compania.id}>
                                            <TableCell>
                                                {compania.numero ? (
                                                    <Badge variant="outline" className="font-mono">
                                                        {compania.numero}°
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">
                                                        CMD
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{compania.nombre}</p>
                                                    {compania.fecha_fundacion && (
                                                        <p className="text-xs text-muted-foreground">
                                                            Fundada: {compania.fecha_fundacion}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {compania.direccion ? (
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <MapPin className="h-3 w-3 text-muted-foreground" />
                                                        {compania.direccion}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm space-y-1">
                                                    {compania.telefono && (
                                                        <div className="flex items-center gap-1">
                                                            <Phone className="h-3 w-3 text-muted-foreground" />
                                                            {compania.telefono}
                                                        </div>
                                                    )}
                                                    {compania.email && (
                                                        <div className="flex items-center gap-1">
                                                            <Mail className="h-3 w-3 text-muted-foreground" />
                                                            {compania.email}
                                                        </div>
                                                    )}
                                                    {!compania.telefono && !compania.email && (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {compania.activa !== false ? (
                                                    <Badge className="bg-green-500">Activa</Badge>
                                                ) : (
                                                    <Badge variant="secondary">Inactiva</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    {tienePermiso('editar_companias') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleOpenDialog(compania)}
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {tienePermiso('eliminar_companias') && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleDelete(compania)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Dialog Crear/Editar */}
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>
                                {editingCompania ? 'Editar Compañía' : 'Nueva Compañía'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingCompania 
                                    ? 'Modifique los datos de la compañía'
                                    : 'Complete los datos para crear una nueva compañía'
                                }
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label>Número (opcional)</Label>
                                    <Input
                                        type="number"
                                        value={formData.numero}
                                        onChange={(e) => setFormData(prev => ({ ...prev, numero: e.target.value ? parseInt(e.target.value) : '' }))}
                                        placeholder="Ej: 1"
                                    />
                                    <p className="text-xs text-muted-foreground">Dejar vacío para Comandancia</p>
                                </div>
                                <div className="col-span-3 space-y-2">
                                    <Label>Nombre *</Label>
                                    <Input
                                        value={formData.nombre}
                                        onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                        placeholder="Primera Compañía de Bomberos o Comandancia"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Dirección</Label>
                                <Input
                                    value={formData.direccion}
                                    onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))}
                                    placeholder="Calle Principal 123"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Teléfono</Label>
                                    <Input
                                        value={formData.telefono}
                                        onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                                        placeholder="+56 9 1234 5678"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="compania@email.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Fecha de Fundación</Label>
                                <Input
                                    type="date"
                                    value={formData.fecha_fundacion}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fecha_fundacion: e.target.value }))}
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowDialog(false)}>
                                Cancelar
                            </Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Guardando...' : editingCompania ? 'Actualizar' : 'Crear'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
}
